import gql from 'graphql-tag';

export const typeDefs = gql`
  type CommerceBusinessAddress {
    country: String
  }

  type SiteCommerce {
    """
    The id of the site.
    """
    id: String
    """
    The site's default country.
    """
    defaultCountry: String
    """
    The site's default currency.
    """
    defaultCurrency: String
    """
    True if the site has enabled quick checkout.
    """
    quickCheckoutEnabled: Boolean
    """
    The site's business address.
    """
    businessAddress: CommerceBusinessAddress
  }

  """
  For a pending order, this is an available shipping method.
  """
  type CommerceOrderAvailableShippingMethod {
    """
    ID for referencing this shipping method.
    """
    id: String
    """
    Shipping method.
    """
    mode: String
    """
    Seller supplied name for this shipping method.
    """
    name: String
    """
    Details about this shipping method.
    """
    description: String
    """
    Price of this shipping method.
    """
    price: CMSPrice
    """
    True if this is the currently selected shipping method.
    """
    selected: Boolean
  }

  """
  Additional item, added by some external integration. Like shipping, or taxes.
  """
  type CommerceOrderExtraItem {
    """
    The name of the plugin that added this extra item.
    """
    pluginName: String
    """
    An plugin-specific id, so a plugin can keep track of multiple line items.
    """
    pluginId: String
    """
    User-friendly name for this line item.
    """
    name: String
    """
    User-friendly description for this line item.
    """
    description: String
    """
    Price of this extra item.
    """
    price: CMSPrice
  }

  type CommerceOrderUserItem {
    """
    The id of the item.
    """
    id: String
    """
    The date and time the item was added to the order.
    """
    addedOn: DateTime
    """
    The date and time the item was last updated.
    """
    updatedOn: DateTime
    """
    The number of items in the order.
    """
    count: Int
    """
    The subscription interval for the item.
    """
    subscriptionInterval: String
    """
    The frequency of charges for an interval.
    """
    subscriptionFrequency: Int
    """
    The number of trial period days for a subscription item.
    """
    subscriptionTrial: Int
    """
    The price of the item.
    """
    price: CMSPrice
    """
    The ItemRef product for the item.
    """
    product: CommerceOrderProduct
    """
    The sku for the item.
    """
    sku: CommerceOrderSku
    """
    The total price for this row of items.
    """
    rowTotal: CMSPrice
  }

  type CommerceOrderCategory {
    """
    True when this category is in archived mode.

    Categories in archived mode will only be visible in Staged environments.
    """
    archived: Boolean
    """
    The CMS Locale ID for the category.
    """
    cmsLocaleId: String
    """
    Boolean to toggle site-indexing for the Item.
    """
    noSearch: Boolean!
    """
    The user who created the category.
    """
    createdBy: String
    """
    The timestamp for when the category was created.
    """
    createdOn: DateTime
    """
    True when this category is in draft mode.
    """
    draft: Boolean
    """
    The full slug for the category. This is the path to the category detail page, e.g. "/en/parent-folder/category/spoons"
    """
    fullSlug: String
    """
    The id of the category.
    """
    id: String
    """
    The name of the category.
    """
    name: String
    """
    The user who published the category.
    """
    publishedBy: String
    """
    The timestamp for when the category was published.
    """
    publishedOn: DateTime
    """
    The slug for the category.
    """
    slug: String
    """
    The user who updated the category.
    """
    updatedBy: String
    """
    The timestamp for when the category was updated.
    """
    updatedOn: DateTime
  }

  type CommerceOrderProduct implements CMSItemCustomFieldAccessors {
    """
    True when this product is in archived mode.

    Products in archived mode will only be visible in Staged environments.
    """
    archived: Boolean
    """
    The CMS Locale ID for the product.
    """
    cmsLocaleId: String
    """
    Boolean to toggle site-indexing for the Item.
    """
    noSearch: Boolean!
    """
    The user who created the product.
    """
    createdBy: String
    """
    The timestamp for when the product was created.
    """
    createdOn: DateTime
    """
    True when this product is in draft mode.
    """
    draft: Boolean
    """
    The full slug for the product. This is the path to the product detail page, e.g. "/en/parent-folder/product/tasty-wooden-spoon"
    """
    fullSlug: String
    """
    The id of the product.
    """
    id: String
    """
    The name of the product.
    """
    name: String
    """
    The user who published the product.
    """
    publishedBy: String
    """
    The timestamp for when the product was published.
    """
    publishedOn: DateTime
    """
    The slug for the product.
    """
    slug: String
    """
    The user who updated the product.
    """
    updatedBy: String
    """
    The timestamp for when the product was updated.
    """
    updatedOn: DateTime
    """
    The categories for the product.
    """
    category: [CommerceOrderCategory]
    """
    The description of the product.
    """
    description: String
    """
    The default sku for the product.
    """
    defaultSku: CommerceOrderSku
    """
    The type of product.
    """
    productType: ProductType
    """
    True if the product is shippable.
    """
    shippable: Boolean
    """
    The properties of the product.
    """
    skuProperties: [CMSCommerceSKUProperty]
    """
    The tax category for the product.
    """
    taxCategory: String
    """
    Accessor for Boolean fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldBool(id: ID, slug: String): Boolean @pickOne

    """
    Accessor for Color fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldColor(id: ID, slug: String): String @pickOne

    """
    Accessor for E-commerce Price fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldCommercePrice(id: ID, slug: String): CMSPrice @pickOne

    """
    Accessor for E-commerce SKU Property Table fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldCommercePropTable(id: ID, slug: String): [CMSCommerceSKUProperty]
      @pickOne

    """
    Accessor for E-commerce SKU Property Value fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldCommercePropValues(id: ID, slug: String): [CMSCommerceSKUValue]
      @pickOne

    """
    Accessor for Date fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldDate(id: ID, slug: String): Date @pickOne

    """
    Accessor for DateTime fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldDateTime(id: ID, slug: String): DateTime @pickOne

    """
    Accessor for Email fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldEmail(id: ID, slug: String): String @pickOne

    """
    Accessor for Enum Option fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldEnumOption(id: ID, slug: String): String @pickOne

    """
    Accessor for External File Reference fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldExtFileRef(id: ID, slug: String): CMSExtFileRefField @pickOne

    """
    Accessor for File Reference fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldFileRef(id: ID, slug: String): CMSFileRefField @pickOne

    # """
    # Accessor for Image fields.

    # Works for both field ids (32-char hex) and field slugs.
    # """
    # fieldImage(id: ID, slug: String): ??? @pickOne

    """
    Accessor for Image Reference fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldImageRef(id: ID, slug: String): CMSImageRefField @pickOne

    """
    Accessor for ItemRef fields.

    This only returns the Id data, it doesn't provide an API to lookup the remote item.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldItemRef(id: ID, slug: String): ID @pickOne

    """
    Accessor for ItemRefSet fields.

    This only returns the Id data, it doesn't provide an API to lookup the remote item.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldItemRefSet(id: ID, slug: String): [ID] @pickOne

    """
    Accessor for Link fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldLink(id: ID, slug: String): String @pickOne

    """
    Accessor for Number fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldNumber(id: ID, slug: String): Float @pickOne

    """
    Accessor for Option fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldOption(id: ID, slug: String): CMSOptionField @pickOne

    """
    Accessor for Phone fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldPhone(id: ID, slug: String): String @pickOne

    """
    Accessor for PlainText fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldPlainText(id: ID, slug: String): String @pickOne

    """
    Accessor for RichText fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldRichText(id: ID, slug: String): RichText @pickOne

    """
    Accessor for Sets (lists) of ExtFileRef fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldSetOfExtFileRef(id: ID, slug: String): [CMSExtFileRefField] @pickOne

    """
    Accessor for Sets (lists) of ImageRef fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldSetOfImageRef(id: ID, slug: String): [CMSImageRefField] @pickOne

    """
    Accessor for Subscription Plan fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldSubscriptionPlan(id: ID, slug: String): CMSCommerceSubscriptionPlan
      @pickOne

    """
    Accessor for User fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldUser(id: ID, slug: String): String @pickOne

    """
    Accessor for Video fields.

    Works for both field ids (32-char hex) and field slugs.
    """
    fieldVideo(id: ID, slug: String): CMSVideoField @pickOne

    """
    Accessor for hydrating the ItemRef.

    Works for both field ids (32-char hex) and field slugs.
    """
    resolveLocalItemRef(id: ID, slug: String): CMSItem @pickOne

    """
    Accessor for hydrating the ItemRefSet.

    Works for both field ids (32-char hex) and field slugs.
    """
    resolveLocalItemRefSet(id: ID, slug: String): [CMSItem] @pickOne
  }

  """
  The type of product.
  """
  type ProductType {
    id: String
    """
    The name of the product type.
    One of: 'physical', 'digital', 'service', or 'advanced'.
    """
    name: String
  }

  """
  A sku for a product.
  """
  type CommerceOrderSku {
    """
    True when this SKU is in archived mode.

    SKUs in archived mode will only be visible in Staged environments.
    """
    archived: Boolean
    """
    The compare at price for the SKU.
    """
    compareAtPrice: CMSPrice
    """
    The CMS Locale ID for the SKU.
    """
    cmsLocaleId: String
    """
    Boolean to toggle site-indexing for the SKU.
    """
    noSearch: Boolean!
    """
    The timestamp for when this SKU was initially created.
    """
    createdOn: DateTime
    """
    The user who created this SKU.
    """
    createdBy: String
    """
    The full slug for the SKU. This is the path to the SKU detail page, e.g. "/sku/tasty-wooden-spoon"
    """
    fullSlug: String
    """
    The files that are downloadable for the SKU.
    """
    downloadFiles: [CMSExtFileRefField]
    """
    True when this SKU is in draft mode.
    """
    draft: Boolean
    """
    Number value of the remaining inventory quantity of this SKU.
    """
    ecSkuInventoryQuantity: Float
    """
    The billing method for the SKU.
    """
    ecSkuBillingMethod: String
    """
    The subscription plan for the SKU.
    """
    ecSkuSubscriptionPlan: CMSCommerceSubscriptionPlan
    """
    The height of the SKU.
    """
    height: Float
    """
    The length of the SKU.
    """
    length: Float
    """
    The name of the SKU.
    """
    name: String
    """
    The main image for the SKU.
    """
    mainImage: CMSImageRefField
    """
    The id of the SKU.
    """
    id: String
    """
    The images for the SKU.
    """
    images: [CMSImageRefField]
    """
    The price of the SKU.
    """
    price: CMSPrice
    """
    The product for the SKU.
    """
    product: CommerceOrderProduct
    """
    The user who published the SKU.
    """
    publishedBy: String
    """
    The timestamp for when the SKU was published.
    """
    publishedOn: DateTime
    """
    The slug for the SKU.
    """
    slug: String
    """
    The sku for the SKU.
    """
    sku: String
    """
    The values for the SKU.
    """
    skuValues: [CMSCommerceSKUValue]
    """
    The user who updated the SKU.
    """
    updatedBy: String
    """
    The timestamp for when the SKU was updated.
    """
    updatedOn: DateTime
    """
    The weight of the SKU.
    """
    weight: Float
    """
    The width of the SKU.
    """
    width: Float
    """
    The inventory for the SKU.
    """
    inventory: CommerceInventory
  }

  """
  The inventory for a product.
  """
  type CommerceInventory {
    """
    The quantity of the inventory.
    """
    quantity: Int
    """
    The type of inventory.
    """
    type: CommerceInventoryType
  }

  """
  The type of inventory for a product.
  """
  enum CommerceInventoryType {
    finite
    infinite
  }

  """
  A file that is downloaded from the site.
  """
  type CommerceOrderDownloadFile {
    """
    The id of the file.
    """
    id: String
    """
    The name of the file.
    """
    name: String
    """
    The URL for the file.
    """
    url: String
  }

  type CommerceOrderAddressValidationEntry {
    """
    The type of validation entry.
    """
    type: String
    """
    An English error message.
    """
    message: String
  }

  type CommerceOrderAddressValidation {
    """
    Warnings with this address. These are not blockers for order submission.
    """
    warnings: [CommerceOrderAddressValidationEntry]
    """
    Errors with this address. These entries will keep an order from submitting, so they should be addressed.
    """
    errors: [CommerceOrderAddressValidationEntry]
  }

  type CommerceOrderAddress {
    """
    Either 'billing' or 'shipping'.
    """
    type: String
    """
    Either null, 'kana' or 'kanji'. Needed for shipping to Japan. See Stripe for further details.
    """
    japanType: String
    """
    The recipient's name.
    """
    addressee: String
    """
    The street address.
    """
    line1: String
    """
    The street address, line 2.
    """
    line2: String
    """
    The city.
    """
    city: String
    """
    The state.
    """
    state: String
    """
    The country.
    """
    country: String
    """
    The postal code.
    """
    postalCode: String
    """
    The address's validation status.
    """
    validation: CommerceOrderAddressValidation
  }

  """
  Identity information for the customer.
  """
  type CommerceOrderIdentity {
    """
    The full name of the customer.
    """
    fullName: String
    """
    The email address of the customer.
    """
    email: String
  }

  type CommerceOrderStripeCardExpiry {
    """
    The year of expiration. (2018+)
    """
    year: Int
    """
    The month of expiration. (1-12)
    """
    month: Int
  }

  type CommerceOrderStripeInfo {
    """
    True if a CC was provided through stripe.
    """
    provided: Boolean
    """
    The last 4 digits of the CC number.
    """
    last4: String
    """
    The issuer of the CC.
    """
    provider: String
    """
    The expiration date of the CC.
    """
    expires: CommerceOrderStripeCardExpiry
  }

  type CommerceOrderStripeCard {
    """
    The provider of the CC.
    """
    card: CommerceOrderStripeInfo
  }

  type CommerceOrderCustomerInfo {
    """
    Stripe payment information.
    """
    stripePayment: CommerceOrderStripeCard
    """
    Identity information.
    """
    identity: CommerceOrderIdentity
    """
    Addresses for the customer.
    """
    addresses: [CommerceOrderAddress]
    """
    The shipping address for the customer.
    """
    shippingAddress: CommerceOrderAddress
    """
    The billing address for the customer.
    """
    billingAddress: CommerceOrderAddress
  }

  """
  Custom data fields for the order.
  """
  type CommerceOrderCustomData {
    """
    The name of the custom data field.
    """
    name: String
    """
    The value of the custom input field.
    """
    textInput: String
    """
    The value of the custom area field.
    """
    textArea: String
    """
    The value of the custom checkbox field.
    """
    checkbox: Boolean
  }

  """
  Payment processor used to process the order.
  """
  enum CommerceOrderPaymentProcessor {
    stripe
    paypal
  }

  type CommerceOrderStatusFlags {
    """
    True if we need more items in the cart before it can be submitted.
    """
    needItems: Boolean
    """
    True if the user needs to provide identity details. (name, email, etc...)
    """
    needIdentity: Boolean
    """
    True if another address needs to be added before the order can be submitted.
    """
    needAddress: Boolean
    """
    True if a shipping address needs to be added before the order can be submitted.
    """
    needShippingAddress: Boolean
    """
    True if a billing address needs to be added before the order can be submitted.
    """
    needBillingAddress: Boolean
    """
    True if payment information needs to attached before the order can be submitted.
    """
    needPayment: Boolean
    """
    True if anything needs to be recalculated before the order can be submitted.
    """
    shouldRecalc: Boolean
    """
    True if any items in the order require shipping.
    """
    requiresShipping: Boolean
    """
    True if any items in the order have download files.
    """
    hasDownloads: Boolean
    """
    True if the order total is zero.
    """
    isFreeOrder: Boolean
    """
    True if the shipping address requires a postal code.
    """
    shippingAddressRequiresPostalCode: Boolean
    """
    True if the billing address requires a postal code.
    """
    billingAddressRequiresPostalCode: Boolean
    """
    True if any items in the order are subscription items.
    """
    hasSubscription: Boolean
  }

  type CommerceOrder {
    id: String
    """
    The date and time the order was started.
    """
    startedOn: DateTime
    """
    The date and time the order was updated.
    """
    updatedOn: DateTime
    """
    The items in the order.
    """
    userItems: [CommerceOrderUserItem]
    """
    The number of items in the order.
    """
    userItemsCount: Int
    """
    Array of additional things that are being added to the order. Like shipping or tax.
    """
    extraItems: [CommerceOrderExtraItem]
    """
    The sum of all purchased items prices.
    """
    subtotal: CMSPrice
    """
    Array of available shipping methods that the customer may select for this order.
    """
    availableShippingMethods: [CommerceOrderAvailableShippingMethod]
    """
    The sum of all purchased items prices, with other things like shipping and taxes.
    """
    total: CMSPrice
    """
    A general-purpose comment section for the Order as a whole.
    """
    comment: String
    """
    Information on the customer.
    """
    customerInfo: CommerceOrderCustomerInfo
    """
    Status flags for the order.
    """
    statusFlags: CommerceOrderStatusFlags
    """
    Custom data fields for the order.
    """
    customData: [CommerceOrderCustomData]
    """
    The payment processor used to process the order.
    """
    paymentProcessor: CommerceOrderPaymentProcessor
    """
    Array of download files for an order.
    """
    downloadFiles: [CommerceOrderDownloadFile]
    """
    Subscription product purchased.
    """
    _purchasedItem: CommerceOrderUserItem
  }

  input CommerceOrderMockOrderArgs {
    """
    The maximum number of SKUs that we'll use when populating this fake order.
    """
    maxItems: Int
    """
    A locale whose items we are mocking.
    """
    locale: ID
  }

  input CommerceOrderFinalizedOrderArgs {
    """
    An accepted order id. Like 'abc-123'.
    """
    orderId: String
    """
    The access token used to verify this order access.
    """
    token: String
  }

  input CMSEnvironmentArgs {
    """
    Select the "staging" environment, which is the work-in-progress version of a Site's CMS, which is viewed and
    managed in the designer and data manager.
    """
    staging: Boolean

    """
    Select a "published" environment, which is a point-in-time snapshot of a Site's CMS, taken when users press the
    "publish" button.

    Published environments are identified by a "publication" id, which is a 24-char hex String.
    """
    publication: ID
  }

  type Commerce {
    commerce: SiteCommerce!
  }

  type Database {
    id: String
    """
    Get the site's commerce orders.
    """
    commerceOrder(
      """
      A mock order is a fake order that is used to test the order flow
      from the designer.
      """
      mockOrder: CommerceOrderMockOrderArgs
      """
      The finalize an order from the live site.
      """
      finalizedOrder: CommerceOrderFinalizedOrderArgs
    ): CommerceOrder
  }

  type Query {
    """
    Get the site's commerce settings.
    """
    site: Commerce
    database: Database
  }
`;
